import { GridColDef } from '@mui/x-data-grid';
import { ethers } from 'ethers';
import * as React from 'react';
import { tokenDecimals } from '../../../../helpers/config/contracts.config';
import { txExplorersPrefixes } from '../../../../helpers/config/explorers';
import { compactTxHash, formatCurrencyAmount } from '../../../../helpers/formatters';
import dayjs from 'dayjs';
import CryptoIcon from "../../../CryptoIcon/CryptoIcon";
import NereusButton from "../../../NereusButton/NereusButton";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import leverageUiColors from "../../../../helpers/config/colors";


export function renderDateTime(dateTimeString: string) {
    const dateTime = dayjs.utc(dateTimeString);
    const date = dateTime.format('YYYY.MM.DD');
    const time = dateTime.format('HH:mm:ss');

    return (
        <div className="text-right">
            <span>{date}</span>&nbsp;
            <span className="nereus-color-neutral-grey">{time}</span>
        </div>
    );
}
export function renderDateTimeMobile(dateTimeString: string) {
    const dateTime = dayjs.utc(dateTimeString);
    const date = dateTime.format('YYYY.MM.DD');
    const time = dateTime.format('HH:mm:ss');

    return (
        <span>{date}<br/><span className="nereus-color-neutral-grey">{time}</span></span>
    );
}

export function renderDate(dateTimeString: string) {
    const dateTime = dayjs(Number(dateTimeString) * 1000);
    const date = dateTime.format('YYYY.MM.DD');

    return (
        <div className="text-right">
            <span>{date}</span>
        </div>
    );
}


const columnConfig: Record<string, GridColDef> = {
    amount: {
        flex: 1,
        field: 'amount',
        headerName: 'Amount',
        headerClassName: 'first-header-column',
        width: 120,
        valueGetter: (params) => Number(params.row.amount),
        renderCell: (params) => (
            <div className="flex items-center" style={{marginLeft: '7px'}}>
                <CryptoIcon ticker="nrs" />
                <div className="ml-3">
                    {formatCurrencyAmount(ethers.utils.formatUnits(BigInt(params.row.amount), tokenDecimals.nrs), 'NRS')}
                </div>
            </div>
        )
    },
    amountMobile: {
        flex: 1,
        sortable: false,
        field: 'amount',
        headerName: 'Amount',
        width: 120,
        valueGetter: (params) => BigInt(params.row.amount),
        renderCell: (params) => {
            const formatted = formatCurrencyAmount(ethers.utils.formatUnits(BigInt(params.row.amount), tokenDecimals.nrs), 'NRS')
            return (
                <div className="flex items-center">
                    {formatted.split(' ')[1]}<br/>
                    {formatted.split(' ')[0]}
                </div>
            )
        }
    },
    dateTime: {
        field: 'dateTime', headerName: 'Date/Time',
        width: 100,
        flex: 1,
        valueGetter: (params) => dayjs(params.row.createdAt).valueOf(),
        renderCell: (params) => renderDateTime(params.row.createdAt)
    },
    dateTimeMobile: {
        field: 'dateTime', headerName: 'Date/Time',
        sortable: false,
        flex: 1,
        valueGetter: (params) => dayjs(params.row.createdAt).valueOf(),
        renderCell: (params) => renderDateTimeMobile(params.row.createdAt)
    },
    type: {
        field: 'type', headerName: 'Type', width: 120,
        sortable: false,
        flex: 1,
        valueGetter: (params) => params.row.type,
    },
    typeMobile: {
        field: 'type', headerName: 'Type',
        sortable: false,
        flex: 1,
        valueGetter: (params) => params.row.type,
        renderCell: (params) => {
            return (
                <div className="flex items-center">
                    {params.row.type.split(' ')[0]}<br/>
                    {params.row.type.split(' ')[1]}
                </div>
            )
        }
    },
    txHash: {
        field: 'txHash', headerName: 'Tx hash', width: 200,
        align: 'right',
        sortable: false,
        headerAlign: 'right',
        renderCell: (params) =>
            params.row.txHash
                ? (
                    <a className="nereus-color-main" href={`${txExplorersPrefixes.base}/${params.row.txHash}`}
                       target="_blank" rel="noreferrer noopemer">
                        <div className="flex gap-2 items-center">
                            <div className="flex">{compactTxHash(params.row.txHash)}</div>
                            <div className="flex-col items-center mr-2">
                                <OpenInNewIcon style={{ height: 18, width: 18, color: leverageUiColors.neutralGrey}}/>
                            </div>
                        </div>
                    </a>
                )
                : ''
    },
    txHashMobile: {
        field: 'txHash', headerName: 'Tx hash',
        align: 'right',
        sortable: false,
        headerAlign: 'right',
        renderCell: (params) =>
            params.row.txHash
                ? (
                    <NereusButton style={{
                        maxWidth: '32px', minWidth: '32px',
                        maxHeight: '32px', minHeight: '32px',
                        padding: 0}} variant="outlined"
                                  color="secondary">
                        <a className="nereus-color-main" href={`${txExplorersPrefixes.base}/${params.row.txHash}`}
                           target="_blank" rel="noreferrer noopemer">
                            <div className="flex gap-2 items-center">
                                <div className="flex">
                                    <OpenInNewIcon style={{ color: leverageUiColors.neutralGrey}}/>
                                </div>
                            </div>
                        </a>
                    </NereusButton>
                )
                : ''
    },
    endTime: {
        field: 'endTime',
        headerName: 'Claimable In',
        width: 120,
        flex: 1,
        valueGetter: (params) => Number(params.row.endTime),
        renderCell: (params) => {
            const now = Math.floor(Date.now() / 1000)
            if (now < Number(params.row.endTime)) {
                const t = dayjs.duration(dayjs(params.row.endTime * 1000).diff(dayjs()))
                if (t.asDays() > 1) {
                    return <>{Math.floor(t.asDays())}&nbsp;<span className="nereus-color-main">Days Left</span></>
                } else {
                    return <span className="nereus-color-main">{dayjs.utc(t.asMilliseconds()).format('HH:mm:ss')} Left</span>
                }
            }

            return <span className="nereus-color-main">Available To Claim</span>
        }
    },
    endTimeMobile: {
        field: 'endTime',
        headerName: 'Claimable In',
        sortable: false,
        width: 120,
        flex: 1,
        valueGetter: (params) => Number(params.row.endTime),
        renderCell: (params) => {
            const now = Math.floor(Date.now() / 1000)
            if (now < Number(params.row.endTime)) {
                const t = dayjs.duration(dayjs(params.row.endTime * 1000).diff(dayjs()))
                if (t.asDays() > 1) {
                    return <span>{Math.floor(t.asDays())}<br/><span className="nereus-color-main">Days&nbsp;Left</span></span>
                } else {
                    return <span className="nereus-color-main">{dayjs.utc(t.asMilliseconds()).format('HH:mm:ss')} Left</span>
                }
            }

            return <span className="nereus-color-main">Available<br/>To Claim</span>
        }
    },
    requestDate: {
        field: 'startTime', headerName: 'Request Date',
        width: 120,
        flex: 1,
        sortable: false,
        valueGetter: (params) => Number(params.row.startTime),
        renderCell: (params) => renderDate(params.row.startTime)
    },
    requestDateMobile: {
        field: 'startTime', headerName: 'Request Date',
        flex: 1,
        sortable: false,
        valueGetter: (params) => Number(params.row.startTime),
        renderCell: (params) => renderDate(params.row.startTime)
    },
};

const columnConfigDynamic: Record<string, any> = {
    statusAction: ({handleClaim}: any) => ({
        field: 'status', headerName: 'Action',
        width: 190,
        align: 'right',
        sortable: false,
        headerAlign: 'right',
        headerClassName: 'last-header-column',
        valueGetter: (params: any) => Number(params.row.endTime),
        renderCell: (params: any) => {
            const now = Math.floor(Date.now() / 1000)
            if (now < Number(params.row.endTime)) {
                return (
                    <div style={{width: 109, marginRight: '7px'}}>
                        <NereusButton variant="outlined" color="secondary"
                                      disabled
                                      className="bg-light-green text-dark-blue w-full">
                            Claim
                        </NereusButton>
                    </div>
                )
            }

            return (
                <div style={{width: 109, marginRight: '7px'}}>
                    <NereusButton variant="contained"
                                  onClick={() => {
                                      handleClaim(params.row.id)
                                  }}
                                  className="bg-light-green text-dark-blue w-full">
                        Claim
                    </NereusButton>
                </div>
            )
        }
    }),
};

export function getLockHistoryColumns() {
    return [
        columnConfig.amount,
        columnConfig.dateTime,
        columnConfig.type,
        columnConfig.txHash,
    ];
}

export function getLockPendingWithdrawalsColumns({handleClaim} : any) {
    return [
        columnConfig.amount,
        columnConfig.endTime,
        columnConfig.requestDate,
        columnConfigDynamic.statusAction({handleClaim}),
    ];
}


export function getLockHistoryColumnsMobile() {
    return [
        columnConfig.amountMobile,
        columnConfig.dateTimeMobile,
        columnConfig.typeMobile,
        columnConfig.txHashMobile,
    ];
}
export function getLockPendingWithdrawalsColumnsMobile({handleClaim} : any) {
    return [
        columnConfig.amountMobile,
        columnConfig.endTimeMobile,
        columnConfig.requestDateMobile,
        columnConfigDynamic.statusAction({handleClaim}),
    ];
}